import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSubscription } from '@packages/api/endpoints/subscription.js';
import { queryKeys } from '@packages/api';
import { GRACE_DAYS } from '@packages/types';
import { useAuth } from '../auth/useAuth';
import { ONBOARDING_SESSION_KEY } from './constants';
import { SubscriptionContext } from './useSubscription';
import { isCancellingWithinPeriod } from './subscription.helpers';
import type { Subscription } from '@packages/types';

/**
 * Returns true when a PAST_DUE subscription is still within the grace window.
 * Mirrors the backend `subscriptionGrantsWriteAccess` predicate.
 * Legacy PAST_DUE rows with pastDueSince == null are treated as inactive.
 */
export function isPastDueWithinGrace(subscription: Subscription): boolean {
  if (subscription.status !== 'PAST_DUE') return false;
  if (subscription.pastDueSince == null) return false;
  const graceCutoff = new Date(subscription.pastDueSince).getTime() + GRACE_DAYS * 24 * 60 * 60 * 1000;
  return graceCutoff > Date.now();
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { accountId } = useAuth();
  const [paymentRequiredDetected, setPaymentRequiredDetected] = useState(false);

  const { data: response, isLoading, isError } = useQuery({
    queryKey: queryKeys.subscription(accountId!),
    queryFn: () => getSubscription(accountId!),
    enabled: !!accountId,
  });

  const subscription = response?.subscription ?? null;
  const hasSubscriptionHistory = response?.hasSubscriptionHistory ?? false;

  // Listen for 402 events dispatched by platform-bridge when a mutation fails
  useEffect(() => {
    function handle() { setPaymentRequiredDetected(true); }
    window.addEventListener('bill:payment-required', handle);
    return () => window.removeEventListener('bill:payment-required', handle);
  }, []);

  // Mirrors the backend subscriptionGrantsWriteAccess predicate:
  // ACTIVE, CANCELLING-within-its-paid-period, and TRIALING-within-trial-period
  // grant access. PAST_DUE within the GRACE_DAYS window also grants access
  // (synchronised with the backend constant so both surfaces agree on the
  // cutoff). Legacy PAST_DUE with pastDueSince == null and everything else is
  // inactive.
  const isActive = useMemo(
    () =>
      !!subscription &&
      (subscription.status === 'ACTIVE' ||
        subscription.status === 'PENDING_PAYMENT_CONFIRMATION' ||
        isCancellingWithinPeriod(subscription) ||
        (subscription.status === 'TRIALING' &&
          !!subscription.trialEnd &&
          new Date(subscription.trialEnd) > new Date()) ||
        isPastDueWithinGrace(subscription)),
    [subscription],
  );

  const needsSubscription = paymentRequiredDetected || (!isLoading && !isError && !isActive);

  // True when the user has clicked Subscribe and may be mid-Paystack checkout.
  // Set in onboarding.tsx before the Paystack redirect; cleared by SubscriptionActivation
  // on success. Prevents the needsOnboarding redirect from firing during the checkout
  // flow when the subscription.create webhook hasn't arrived yet.
  const isCheckoutPending = useMemo(() => !!sessionStorage.getItem(ONBOARDING_SESSION_KEY), []);

  // New account that has never had a subscription - must complete onboarding first.
  // Distinct from a returning user whose subscription has lapsed (they see the banner instead).
  const needsOnboarding = !isLoading && !isError && !isActive && !hasSubscriptionHistory
    && !paymentRequiredDetected && !isCheckoutPending;

  return (
    <SubscriptionContext value={{ subscription, isLoading, needsSubscription, needsOnboarding }}>
      {children}
    </SubscriptionContext>
  );
}
